import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import axios from 'axios';
import { X, MessageCircle, Printer, FileText, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import { useCompanySettings } from '../hooks/useCompanySettings';
import { overlayClose } from '../lib/overlayClose';
import { printHtmlSilently } from '../lib/silentPrint';
import { ChargeModal } from './ChargeModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;
const fmtDate = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return '-'; }
};
const fmtDateTime = (d) => {
  if (!d) return '-';
  try { return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return '-'; }
};

// wa.me exige apenas dígitos, com DDI. Assume Brasil (55) quando faltar.
const buildWaNumber = (phone) => {
  let digits = (phone || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length <= 11) digits = `55${digits}`;
  return digits;
};

export const CustomerStatementModal = ({ customerId, onClose }) => {
  const company = useCompanySettings();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('extrato');
  const [paidData, setPaidData] = useState(null);
  const [paidLoading, setPaidLoading] = useState(false);
  const [openData, setOpenData] = useState(null);
  const [openLoading, setOpenLoading] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState([]);
  const [chargeOpen, setChargeOpen] = useState(false);
  const [expandedNote, setExpandedNote] = useState(null);
  const [periodFrom, setPeriodFrom] = useState('');
  const [periodTo, setPeriodTo] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/customers/${customerId}/statement`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar extrato');
    } finally { setLoading(false); }
  }, [customerId]);

  const loadPaid = useCallback(async () => {
    setPaidLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/paid-notes`, { withCredentials: true });
      setPaidData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar notas pagas');
    } finally { setPaidLoading(false); }
  }, [customerId]);

  const loadOpen = useCallback(async () => {
    setOpenLoading(true);
    try {
      const res = await axios.get(`${API}/customers/${customerId}/open-notes`, { withCredentials: true });
      setOpenData(res.data);
      setSelectedNotes([]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar notas em aberto');
    } finally { setOpenLoading(false); }
  }, [customerId]);

  useEffect(() => { if (customerId) load(); }, [customerId, load]);
  useEffect(() => { if (tab === 'abertas' && customerId) loadOpen(); }, [tab, customerId, loadOpen]);
  useEffect(() => { if (tab === 'pagas' && customerId && paidData === null) loadPaid(); }, [tab, customerId, paidData, loadPaid]);

  if (!customerId) return null;

  const cust = data?.customer;
  const totals = data?.totals;
  const events = data?.events || [];

  const buildTextMessage = () => {
    if (!data) return '';
    const lines = [];
    lines.push(`*${company?.trade_name || 'Smart Flow'}* — Extrato de Venda a Prazo`);
    lines.push(`Cliente: ${cust.name}`);
    lines.push('--------------------------------');
    events.forEach((e) => {
      const sign = e.type === 'compra' ? '+' : '-';
      const tag = e.type === 'compra' ? '🛒 Compra' : '💰 Pagamento';
      lines.push(`${fmtDate(e.date)} ${tag} ${sign}${fmt(e.amount)}`);
    });
    lines.push('--------------------------------');
    lines.push(`Total em compras: ${fmt(totals.total_purchases)}`);
    lines.push(`Total pago: ${fmt(totals.total_paid)}`);
    lines.push(`*Saldo devedor: ${fmt(totals.outstanding)}*`);
    if (cust.credit_limit > 0) lines.push(`Limite de crédito: ${fmt(cust.credit_limit)}`);
    return lines.join('\n');
  };

  const handleWhatsApp = () => {
    const num = buildWaNumber(cust?.phone);
    const text = encodeURIComponent(buildTextMessage());
    const url = num ? `https://wa.me/${num}?text=${text}` : `https://wa.me/?text=${text}`;
    window.open(url, '_blank');
  };

  const handlePrint = () => {
    if (!data) return;
    const rows = events.map((e) => `
      <tr>
        <td>${fmtDate(e.date)}</td>
        <td>${e.type === 'compra' ? 'Compra' + (e.sale_number ? ' #' + e.sale_number : '') : 'Pagamento'}</td>
        <td style="text-align:right">${e.type === 'compra' ? '+' : '-'}${fmt(e.amount)}</td>
        <td style="text-align:right">${fmt(e.running_balance)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Extrato ${cust.name}</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { font-family: 'Courier New', monospace; }
        body { width: 76mm; font-size: 11px; color: #000; margin: 0; }
        h2 { text-align:center; font-size: 13px; margin: 4px 0; }
        .center { text-align:center; }
        table { width:100%; border-collapse: collapse; margin-top: 4px; }
        th, td { padding: 1px 0; font-size: 10px; }
        th { border-bottom: 1px dashed #000; text-align:left; }
        .divider { border-top: 1px dashed #000; margin: 4px 0; }
        .tot { display:flex; justify-content:space-between; font-size: 11px; }
        .bold { font-weight: bold; }
        .big { font-size: 14px; }
      </style></head><body>
      <h2>${company?.trade_name || 'Smart Flow'}</h2>
      <div class="center">${company?.address || ''}</div>
      <div class="center">${company?.phone ? 'Fone: ' + company.phone : ''}</div>
      <div class="divider"></div>
      <div class="center bold">EXTRATO DE VENDA A PRAZO</div>
      <div>Cliente: ${cust.name}</div>
      ${cust.cpf_cnpj ? `<div>Doc: ${cust.cpf_cnpj}</div>` : ''}
      ${cust.phone ? `<div>Fone: ${cust.phone}</div>` : ''}
      <div>Emitido: ${fmtDateTime(data.generated_at)}</div>
      <div class="divider"></div>
      <table>
        <thead><tr><th>Data</th><th>Mov.</th><th style="text-align:right">Valor</th><th style="text-align:right">Saldo</th></tr></thead>
        <tbody>${rows || '<tr><td colspan="4" class="center">Sem movimentações</td></tr>'}</tbody>
      </table>
      <div class="divider"></div>
      <div class="tot"><span>Total compras:</span><span>${fmt(totals.total_purchases)}</span></div>
      <div class="tot"><span>Total pago:</span><span>${fmt(totals.total_paid)}</span></div>
      <div class="tot bold big"><span>SALDO DEVEDOR:</span><span>${fmt(totals.outstanding)}</span></div>
      ${cust.credit_limit > 0 ? `<div class="tot"><span>Limite crédito:</span><span>${fmt(cust.credit_limit)}</span></div>` : ''}
      <div class="divider"></div>
      <div class="center">*** Confira seus valores ***</div>
      </body></html>`;
    printHtmlSilently(html);
  };

  const localYMD = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };
  const inPeriod = (n) => {
    const y = localYMD(n.paid_at || n.created_at);
    if (periodFrom && (!y || y < periodFrom)) return false;
    if (periodTo && (!y || y > periodTo)) return false;
    return true;
  };
  const filteredNotes = (paidData?.notes || []).filter(inPeriod);
  const filteredTotal = filteredNotes.reduce((s, n) => s + Number(n.amount_paid || 0), 0);
  const isFiltered = !!(periodFrom || periodTo);

  const applyThisMonth = () => {
    const now = new Date();
    setPeriodFrom(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`);
    setPeriodTo(localYMD(now.toISOString()));
  };
  const applyLastMonth = () => {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const last = new Date(now.getFullYear(), now.getMonth(), 0);
    const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    setPeriodFrom(ymd(first));
    setPeriodTo(ymd(last));
  };
  const clearPeriod = () => { setPeriodFrom(''); setPeriodTo(''); };
  const periodLabel = () => {
    if (!isFiltered) return 'Todo o período';
    const f = periodFrom ? fmtDate(periodFrom + 'T12:00:00') : 'início';
    const t = periodTo ? fmtDate(periodTo + 'T12:00:00') : 'hoje';
    return `${f} a ${t}`;
  };

  const paidHeader = () => `
      <h2>${company?.trade_name || 'Smart Flow'}</h2>
      <div class="center">${company?.address || ''}</div>
      <div class="center">${company?.phone ? 'Fone: ' + company.phone : ''}</div>
      <div class="divider"></div>`;

  const printPaidNote = (note) => {
    if (!note) return;
    const c = paidData?.customer || cust || {};
    const itemRows = (note.items || []).map((it) => `
      <tr>
        <td>${(it.product_name || '').substring(0, 22)}</td>
        <td style="text-align:right">${Number(it.quantity || 0).toString().replace('.', ',')}</td>
        <td style="text-align:right">${fmt(it.subtotal)}</td>
      </tr>`).join('');
    const statusBox = note.status === 'quitada'
      ? `<div class="badge">✓ NOTA QUITADA</div>`
      : `<div class="badge parc">PAGAMENTO PARCIAL<br/>RESTA: ${fmt(note.remaining)}</div>`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Nota ${note.sale_number || ''}</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { font-family: 'Courier New', monospace; }
        body { width: 76mm; font-size: 11px; color: #000; margin: 0; }
        h2 { text-align:center; font-size: 13px; margin: 4px 0; }
        .center { text-align:center; }
        table { width:100%; border-collapse: collapse; margin-top: 4px; }
        th, td { padding: 1px 0; font-size: 10px; }
        th { border-bottom: 1px dashed #000; text-align:left; }
        .divider { border-top: 1px dashed #000; margin: 4px 0; }
        .tot { display:flex; justify-content:space-between; font-size: 11px; }
        .bold { font-weight: bold; }
        .big { font-size: 14px; }
        .badge { text-align:center; font-weight:bold; font-size:13px; border:1px solid #000; padding:4px; margin-top:4px; }
        .badge.parc { border-style:dashed; }
      </style></head><body>
      ${paidHeader()}
      <div class="center bold">COMPROVANTE DE PAGAMENTO</div>
      <div>Cliente: ${c.name || '—'}</div>
      ${note.sale_number ? `<div>Venda nº: #${note.sale_number}</div>` : ''}
      <div>Conta: ${(note.description || '—').substring(0, 40)}</div>
      ${note.paid_at ? `<div>Pago em: ${fmtDateTime(note.paid_at)}</div>` : ''}
      <div>Forma: ${note.payment_method_label || '—'}</div>
      <div class="divider"></div>
      ${itemRows ? `<table>
        <thead><tr><th>Produto</th><th style="text-align:right">Qtd</th><th style="text-align:right">Subtotal</th></tr></thead>
        <tbody>${itemRows}</tbody>
      </table><div class="divider"></div>` : '<div class="center">(sem produtos vinculados)</div><div class="divider"></div>'}
      <div class="tot"><span>Valor da conta:</span><span>${fmt(note.amount)}</span></div>
      <div class="tot bold"><span>Total pago:</span><span>${fmt(note.amount_paid)}</span></div>
      ${statusBox}
      <div class="divider"></div>
      <div class="center">Emitido: ${fmtDateTime(new Date().toISOString())}</div>
      <div class="center" style="font-size:9px;margin-top:4px">Smart Flow · Comprovante para o cliente</div>
      </body></html>`;
    printHtmlSilently(html);
  };

  const printPaidList = () => {
    const notes = filteredNotes;
    if (notes.length === 0) return;
    const c = paidData?.customer || cust || {};
    const rows = notes.map((n) => `
      <tr>
        <td>${fmtDate(n.paid_at || n.created_at)}</td>
        <td>${n.sale_number ? '#' + n.sale_number : '—'}</td>
        <td>${n.status === 'quitada' ? 'QUIT' : 'PARC'}</td>
        <td style="text-align:right">${fmt(n.amount_paid)}</td>
      </tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Notas Pagas ${c.name || ''}</title>
      <style>
        @page { size: 80mm auto; margin: 2mm; }
        * { font-family: 'Courier New', monospace; }
        body { width: 76mm; font-size: 11px; color: #000; margin: 0; }
        h2 { text-align:center; font-size: 13px; margin: 4px 0; }
        .center { text-align:center; }
        table { width:100%; border-collapse: collapse; margin-top: 4px; }
        th, td { padding: 1px 0; font-size: 10px; }
        th { border-bottom: 1px dashed #000; text-align:left; }
        .divider { border-top: 1px dashed #000; margin: 4px 0; }
        .tot { display:flex; justify-content:space-between; font-size: 12px; }
        .bold { font-weight: bold; }
        .big { font-size: 14px; }
      </style></head><body>
      ${paidHeader()}
      <div class="center bold">EXTRATO DE PAGAMENTOS</div>
      <div>Cliente: ${c.name || '—'}</div>
      <div>Período: ${periodLabel()}</div>
      <div>Emitido: ${fmtDateTime(new Date().toISOString())}</div>
      <div class="divider"></div>
      <table>
        <thead><tr><th>Data</th><th>Venda</th><th>Status</th><th style="text-align:right">Pago</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="divider"></div>
      <div class="tot bold big"><span>TOTAL PAGO:</span><span>${fmt(filteredTotal)}</span></div>
      <div class="tot"><span>Notas:</span><span>${notes.length}</span></div>
      <div class="divider"></div>
      <div class="center">*** Confira seus valores ***</div>
      </body></html>`;
    printHtmlSilently(html);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto" {...overlayClose(onClose)} data-testid="statement-modal-overlay">
      <div className="bg-white rounded-lg w-full max-w-2xl my-2 max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="statement-modal">
        <div className="flex items-center justify-between p-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <FileText size={20} className="text-blue-600" />
            <h3 className="text-lg font-bold text-slate-900">Extrato de Venda a Prazo</h3>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" data-testid="statement-close-btn"><X size={20} /></button>
        </div>

        <div className="p-4">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="grid grid-cols-3 w-full mb-4">
              <TabsTrigger value="extrato" data-testid="tab-extrato">Extrato</TabsTrigger>
              <TabsTrigger value="abertas" data-testid="tab-notas-abertas">Notas em Aberto</TabsTrigger>
              <TabsTrigger value="pagas" data-testid="tab-notas-pagas">Notas Pagas</TabsTrigger>
            </TabsList>

            <TabsContent value="abertas">
              {openLoading && <p className="text-center text-slate-400 py-8" data-testid="open-loading">Carregando notas em aberto...</p>}
              {!openLoading && openData && (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4" data-testid="open-notes-summary">
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><p className="text-[10px] uppercase font-bold text-slate-400">Notas em aberto</p><p className="text-xl font-black text-slate-800" data-testid="open-notes-count">{openData.totals.count}{openData.totals.overdue_count > 0 && <span className="text-xs text-red-600 font-bold ml-2">· {openData.totals.overdue_count} vencida(s)</span>}</p></div>
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3"><p className="text-[10px] uppercase font-bold text-slate-400">Total das notas</p><p className="text-xl font-black text-slate-800">R$ {openData.totals.total_amount.toFixed(2)}</p></div>
                    <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3"><p className="text-[10px] uppercase font-bold text-emerald-700">Já pago</p><p className="text-xl font-black text-emerald-700">R$ {openData.totals.total_paid.toFixed(2)}</p></div>
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3"><p className="text-[10px] uppercase font-bold text-red-700">Saldo devedor</p><p className="text-xl font-black text-red-700" data-testid="open-notes-remaining">R$ {openData.totals.total_remaining.toFixed(2)}</p></div>
                  </div>
                  {openData.notes.length === 0 ? (
                    <p className="text-center text-slate-400 py-8" data-testid="open-notes-empty">Nenhuma nota em aberto — cliente em dia.</p>
                  ) : (
                    <>
                    {(() => {
                      const sel = openData.notes.filter((n) => selectedNotes.includes(n.id));
                      const selTotal = sel.reduce((a, n) => a + n.remaining, 0);
                      const allIds = openData.notes.map((n) => n.id);
                      const overdueIds = openData.notes.filter((n) => n.days_overdue > 0).map((n) => n.id);
                      return (
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3 flex flex-wrap items-center gap-2" data-testid="charge-box">
                          <div className="flex-1 min-w-[200px]">
                            <p className="text-[10px] uppercase font-bold text-blue-700">Cobrança</p>
                            <p className="text-sm text-slate-700">
                              {sel.length === 0 ? 'Marque as notas que deseja cobrar.' : <><b>{sel.length}</b> nota(s) selecionada(s) · total <b className="text-red-700" data-testid="charge-selected-total">R$ {selTotal.toFixed(2)}</b></>}
                            </p>
                          </div>
                          <div className="flex flex-wrap gap-1">
                            <Button size="sm" variant="outline" onClick={() => setSelectedNotes(allIds)} data-testid="charge-select-all">Todas</Button>
                            {overdueIds.length > 0 && <Button size="sm" variant="outline" onClick={() => setSelectedNotes(overdueIds)} data-testid="charge-select-overdue">Só vencidas</Button>}
                            {sel.length > 0 && <Button size="sm" variant="ghost" onClick={() => setSelectedNotes([])} data-testid="charge-select-none">Limpar</Button>}
                            <Button size="sm" disabled={sel.length === 0} onClick={() => setChargeOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="charge-generate-btn"><FileText size={14} className="mr-1" /> Gerar Cobrança</Button>
                          </div>
                        </div>
                      );
                    })()}
                    <div className="border border-slate-200 rounded-lg overflow-hidden">
                      <table className="w-full text-sm" data-testid="open-notes-table">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="px-2 py-2 w-8"><input type="checkbox" aria-label="Selecionar todas" checked={selectedNotes.length === openData.notes.length} onChange={(e) => setSelectedNotes(e.target.checked ? openData.notes.map((n) => n.id) : [])} data-testid="open-notes-check-all" /></th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Nota</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Data</th>
                            <th className="px-3 py-2 text-left text-[10px] uppercase font-bold text-slate-500">Vencimento</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-slate-500">Valor total</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-slate-500">Já pago</th>
                            <th className="px-3 py-2 text-right text-[10px] uppercase font-bold text-slate-500">Saldo devedor</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200">
                          {openData.notes.map((n) => (
                            <tr key={n.id} className={`${n.days_overdue > 0 ? 'bg-red-50/60' : ''} ${selectedNotes.includes(n.id) ? 'ring-1 ring-inset ring-blue-400' : ''} cursor-pointer`} onClick={() => setSelectedNotes((prev) => prev.includes(n.id) ? prev.filter((x) => x !== n.id) : [...prev, n.id])} data-testid={`open-note-${n.id}`}>
                              <td className="px-2 py-2"><input type="checkbox" checked={selectedNotes.includes(n.id)} onClick={(e) => e.stopPropagation()} onChange={() => setSelectedNotes((prev) => prev.includes(n.id) ? prev.filter((x) => x !== n.id) : [...prev, n.id])} data-testid={`open-note-check-${n.id}`} /></td>
                              <td className="px-3 py-2 font-mono font-bold">{n.sale_number != null ? `#${n.sale_number}` : '—'}<span className="block text-[10px] font-sans font-normal text-slate-400">{n.description}</span></td>
                              <td className="px-3 py-2">{n.created_at ? new Date(n.created_at).toLocaleDateString('pt-BR') : '—'}</td>
                              <td className="px-3 py-2">
                                {n.due_date ? new Date(n.due_date).toLocaleDateString('pt-BR') : '—'}
                                {n.days_overdue > 0 && <span className="block text-[10px] font-bold text-red-600">{n.days_overdue} dia(s) em atraso</span>}
                              </td>
                              <td className="px-3 py-2 text-right font-mono whitespace-nowrap">R$ {n.amount.toFixed(2)}</td>
                              <td className="px-3 py-2 text-right font-mono text-emerald-700 whitespace-nowrap">{n.amount_paid > 0 ? `R$ ${n.amount_paid.toFixed(2)}` : '—'}{n.status === 'parcial' && <span className="block text-[10px] text-slate-400">{n.payments_count} pgto(s)</span>}</td>
                              <td className="px-3 py-2 text-right font-mono font-black text-red-700 whitespace-nowrap">R$ {n.remaining.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-slate-100 font-bold">
                          <tr>
                            <td colSpan={4} className="px-3 py-2 text-right text-xs uppercase">Total</td>
                            <td className="px-3 py-2 text-right font-mono whitespace-nowrap">R$ {openData.totals.total_amount.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-emerald-700 whitespace-nowrap">R$ {openData.totals.total_paid.toFixed(2)}</td>
                            <td className="px-3 py-2 text-right font-mono text-red-700 whitespace-nowrap">R$ {openData.totals.total_remaining.toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                    </>
                  )}
                </>
              )}
              {chargeOpen && openData && (
                <ChargeModal
                  company={company}
                  customer={openData.customer}
                  notes={openData.notes.filter((n) => selectedNotes.includes(n.id))}
                  onClose={() => setChargeOpen(false)}
                />
              )}
            </TabsContent>
            <TabsContent value="extrato">
          {loading && <p className="text-center text-slate-400 py-8" data-testid="statement-loading">Carregando extrato...</p>}
          {error && <p className="text-center text-red-600 py-8">{error}</p>}

          {!loading && !error && data && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                <div className="md:col-span-2">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Cliente</p>
                  <p className="text-lg font-bold text-slate-900" data-testid="statement-customer-name">{cust.name}</p>
                  {cust.phone && <p className="text-xs text-slate-500">{cust.phone}</p>}
                </div>
                <div className="bg-slate-50 rounded p-2 border border-slate-200">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Limite</p>
                  <p className="text-base font-bold text-blue-600">{fmt(cust.credit_limit)}</p>
                </div>
                <div className="bg-red-50 rounded p-2 border border-red-200">
                  <p className="text-[10px] uppercase tracking-wider font-bold text-red-400">Saldo Devedor</p>
                  <p className="text-base font-bold text-red-600" data-testid="statement-balance">{fmt(totals.outstanding)}</p>
                </div>
              </div>

              <div className="flex flex-wrap gap-2 mb-4">
                <Button onClick={handleWhatsApp} className="bg-green-600 hover:bg-green-700 text-white" data-testid="statement-whatsapp-btn">
                  <MessageCircle size={16} className="mr-2" /> Enviar por WhatsApp
                </Button>
                <Button onClick={handlePrint} variant="outline" data-testid="statement-print-btn">
                  <Printer size={16} className="mr-2" /> Imprimir (80mm)
                </Button>
              </div>

              <div className="border border-slate-200 rounded overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-b border-slate-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Data</th>
                      <th className="px-3 py-2 text-left text-[11px] font-bold uppercase tracking-wider text-slate-500">Movimentação</th>
                      <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Valor</th>
                      <th className="px-3 py-2 text-right text-[11px] font-bold uppercase tracking-wider text-slate-500">Saldo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-testid="statement-events">
                    {events.length === 0 && <tr><td colSpan={4} className="text-center py-6 text-slate-400">Nenhuma movimentação de fiado</td></tr>}
                    {events.map((e, i) => (
                      <tr key={i} className={e.type === 'pagamento' ? 'bg-emerald-50/40' : ''}>
                        <td className="px-3 py-2 text-slate-600">{fmtDate(e.date)}</td>
                        <td className="px-3 py-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-[11px] font-bold ${e.type === 'compra' ? (e.is_opening_balance ? 'bg-indigo-100 text-indigo-700' : 'bg-amber-100 text-amber-700') : 'bg-emerald-100 text-emerald-700'}`}>
                            {e.type === 'compra' ? (e.is_opening_balance ? '📋 Saldo de abertura' : '🛒 Compra') : '💰 Pagamento'}
                          </span>
                          {e.sale_number ? <span className="text-xs text-slate-400 ml-2">#{e.sale_number}</span> : null}
                        </td>
                        <td className={`px-3 py-2 text-right font-bold ${e.type === 'compra' ? 'text-amber-700' : 'text-emerald-700'}`}>
                          {e.type === 'compra' ? '+' : '-'}{fmt(e.amount)}
                        </td>
                        <td className="px-3 py-2 text-right font-medium text-slate-900">{fmt(e.running_balance)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-slate-50 border-t-2 border-slate-200">
                    <tr>
                      <td colSpan={2} className="px-3 py-2 text-right text-xs font-bold text-slate-500">Total compras / pago</td>
                      <td className="px-3 py-2 text-right text-xs text-slate-600">{fmt(totals.total_purchases)} / {fmt(totals.total_paid)}</td>
                      <td className="px-3 py-2 text-right font-black text-red-600">{fmt(totals.outstanding)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </>
          )}
            </TabsContent>

            <TabsContent value="pagas">
              {paidLoading && <p className="text-center text-slate-400 py-8" data-testid="paid-loading">Carregando notas pagas...</p>}
              {!paidLoading && !paidData && error && (
                <div className="text-center py-8" data-testid="paid-error">
                  <p className="text-red-600 mb-3">{error}</p>
                  <Button variant="outline" onClick={() => { setError(''); loadPaid(); }} data-testid="paid-retry-btn">Tentar novamente</Button>
                </div>
              )}
              {!paidLoading && paidData && (
                <>
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4">
                    <div className="flex flex-wrap items-end gap-3">
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">De</label>
                        <input type="date" value={periodFrom} onChange={(e) => setPeriodFrom(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid="paid-period-from" />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold text-slate-400 block mb-1">Até</label>
                        <input type="date" value={periodTo} onChange={(e) => setPeriodTo(e.target.value)} className="border border-slate-300 rounded px-2 py-1 text-sm" data-testid="paid-period-to" />
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={applyThisMonth} data-testid="paid-preset-month">Este mês</Button>
                        <Button size="sm" variant="outline" onClick={applyLastMonth} data-testid="paid-preset-lastmonth">Mês passado</Button>
                        {isFiltered && <Button size="sm" variant="ghost" onClick={clearPeriod} data-testid="paid-preset-clear">Limpar</Button>}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Total pago{isFiltered ? ' (período)' : ''}</p>
                      <p className="text-lg font-bold text-emerald-600" data-testid="paid-total">{fmt(filteredTotal)} <span className="text-xs text-slate-400 font-normal">· {filteredNotes.length} nota(s)</span></p>
                      <p className="text-[11px] text-slate-400" data-testid="paid-period-label">{periodLabel()}</p>
                    </div>
                    <Button onClick={printPaidList} variant="outline" disabled={filteredNotes.length === 0} data-testid="print-paid-list-btn">
                      <Printer size={16} className="mr-2" /> Imprimir lista
                    </Button>
                  </div>
                  {filteredNotes.length === 0 && <p className="text-center py-8 text-slate-400" data-testid="paid-empty">{isFiltered ? 'Nenhuma nota paga neste período' : 'Nenhuma nota paga ainda'}</p>}
                  <div className="space-y-2" data-testid="paid-notes-list">
                    {filteredNotes.map((n) => {
                      const isOpen = expandedNote === n.id;
                      return (
                        <div key={n.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`paid-note-${n.id}`}>
                          <div className="flex items-center gap-2 p-3 bg-slate-50">
                            <button onClick={() => setExpandedNote(isOpen ? null : n.id)} className="text-slate-500 hover:text-slate-800" data-testid={`paid-note-toggle-${n.id}`}>
                              {isOpen ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-bold text-slate-900 text-sm truncate">{n.description}</span>
                                {n.status === 'quitada'
                                  ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold bg-emerald-100 text-emerald-700"><CheckCircle2 size={11} /> QUITADA</span>
                                  : <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-700">PARCIAL</span>}
                              </div>
                              <p className="text-xs text-slate-500">{n.paid_at ? fmtDate(n.paid_at) : fmtDate(n.created_at)} · {n.payment_method_label}{n.sale_number ? ` · #${n.sale_number}` : ''}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-sm font-bold text-emerald-600">{fmt(n.amount_paid)}</p>
                              {n.status === 'parcial' && <p className="text-[10px] text-red-500">resta {fmt(n.remaining)}</p>}
                            </div>
                            <Button size="sm" variant="outline" onClick={() => printPaidNote(n)} title="Imprimir esta nota" data-testid={`print-paid-note-${n.id}`}>
                              <Printer size={14} />
                            </Button>
                          </div>
                          {isOpen && (
                            <div className="p-3 border-t border-slate-100" data-testid={`paid-note-items-${n.id}`}>
                              {(n.items || []).length === 0
                                ? <p className="text-xs text-slate-400">Sem produtos vinculados a esta nota.</p>
                                : (
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-[11px] uppercase text-slate-400">
                                        <th className="text-left font-bold py-1">Produto</th>
                                        <th className="text-right font-bold py-1">Qtd</th>
                                        <th className="text-right font-bold py-1">Preço</th>
                                        <th className="text-right font-bold py-1">Subtotal</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {n.items.map((it, ix) => (
                                        <tr key={ix} className="border-t border-slate-50">
                                          <td className="py-1 text-slate-700">{it.product_name}</td>
                                          <td className="py-1 text-right text-slate-600">{Number(it.quantity)}</td>
                                          <td className="py-1 text-right text-slate-600">{fmt(it.unit_price)}</td>
                                          <td className="py-1 text-right font-medium text-slate-900">{fmt(it.subtotal)}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                )}
                              <div className="flex justify-between mt-2 pt-2 border-t border-slate-200 text-xs">
                                <span className="text-slate-500">Valor da conta: <strong>{fmt(n.amount)}</strong></span>
                                <span className="text-emerald-600">Pago: <strong>{fmt(n.amount_paid)}</strong></span>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};
